import { collection, doc, getDoc, getDocs, limit, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FS } from '../shared/constants/firestorePaths';
import { FIREBASE_CONFIG } from '../config/Config';

const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
const clean = (value) => String(value || '').trim();

const buildDocumentRefs = (documentId) => {
  const safeId = clean(documentId);
  if (!safeId) return [];
  const refs = [];
  if (appId) refs.push(doc(db, ...FS.documents(appId), safeId));
  refs.push(doc(db, ...FS.rootDocuments(safeId)));
  return refs;
};

export const readPublishedDocument = async (documentId) => {
  const refs = buildDocumentRefs(documentId);
  for (const candidateRef of refs) {
    try {
      const snap = await getDoc(candidateRef);
      if (!snap.exists()) continue;
      return { id: snap.id, ref: candidateRef, data: { id: snap.id, ...(snap.data() || {}) } };
    } catch (e) {
      void e;
    }
  }
  return null;
};

export const readLatestPublishedDocumentInSeries = async ({ documentId = '', publicationSeriesId = '' }) => {
  const initial = await readPublishedDocument(documentId || publicationSeriesId);
  const seed = initial?.data || null;
  const seriesId = clean(publicationSeriesId || seed?.publicationSeriesId || seed?.id || documentId);
  if (!seriesId) return initial;

  const refs = [];
  if (appId) refs.push(collection(db, ...FS.documents(appId)));
  refs.push(collection(db, ...FS.rootDocumentsCol()));

  let best = initial?.data ? initial : null;
  let bestVersion = Math.max(0, Number(initial?.data?.publicationVersion || 0) || 0);

  for (const colRef of refs) {
    try {
      const snap = await getDocs(query(colRef, where('publicationSeriesId', '==', seriesId), limit(50)));
      snap.docs.forEach((d) => {
        const data = { id: d.id, ...(d.data() || {}) };
        const version = Math.max(0, Number(data?.publicationVersion || 0) || 0);
        if (!best || version > bestVersion) {
          best = { id: d.id, ref: d.ref, data };
          bestVersion = version;
          return;
        }
        if (best && version === bestVersion) {
          const nextTime = data?.updatedAt?.toDate ? data.updatedAt.toDate().getTime() : data?.createdAt?.toDate ? data.createdAt.toDate().getTime() : 0;
          const bestTime = best?.data?.updatedAt?.toDate ? best.data.updatedAt.toDate().getTime() : best?.data?.createdAt?.toDate ? best.data.createdAt.toDate().getTime() : 0;
          if (nextTime > bestTime) best = { id: d.id, ref: d.ref, data };
        }
      });
    } catch (e) {
      void e;
    }
  }

  return best || initial;
};

export { serverTimestamp } from 'firebase/firestore';

export const patchDocumentEverywhere = async (documentId, patch) => {
  const refs = buildDocumentRefs(documentId);
  let wrote = false;

  for (const candidateRef of refs) {
    try {
      const snap = await getDoc(candidateRef);
      if (!snap.exists()) continue;
      await updateDoc(candidateRef, patch);
      wrote = true;
    } catch (e) {
      void e;
    }
  }

  if (!wrote && appId) {
    try {
      await setDoc(doc(db, ...FS.documents(appId), clean(documentId)), patch, { merge: true });
      wrote = true;
    } catch (e) {
      void e;
    }
  }

  return wrote;
};
